import { assertNever } from '@spiaggeit/spit-core'
import head from 'lodash/head'
import { ApiBoughtService } from '@/models/cart'
import {
  BoughServiceByTimeSchedule,
  BoughtService,
  BoughtServicesRecord,
} from '@/models/quote'
import { Service, ServicePriceMode, TimeSchedule } from '@/models/service'

export function toApiBoughtServices(
  boughtServices: BoughtService[]
): ApiBoughtService[] {
  return boughtServices.reduce<ApiBoughtService[]>((acc, boughtService) => {
    if (boughtService.bought === 0) {
      return acc
    }

    boughtService.days.forEach((day) => {
      acc.push({
        bought: boughtService.bought,
        day,
        endTime: boughtService.timeSchedule.endTime,
        name: boughtService.timeSchedule.name,
        serviceId: boughtService.serviceId,
        startTime: boughtService.timeSchedule.startTime,
      })
    })

    return acc
  }, [])
}

export const SINGLE_DAY_PRICE_MODES: readonly ServicePriceMode[] = [
  ServicePriceMode.FIXED,
  ServicePriceMode.BEDS,
]

export function getDaysFromPriceMode(
  service: Service,
  dayDifference: number
): number[] {
  switch (service.scheduleType) {
    case 'daily': {
      const firstSchedule = head(service.timeSchedules)

      if (!firstSchedule) {
        // throw new Error(`no schedule found for service`)
        return []
      }

      const availableDays = Object.entries(service.dailyTimeSchedules).reduce<
        number[]
      >((acc, entry) => {
        const timeSchedules = entry[1]
        if (timeSchedules.length !== 1) {
          // daily time schedule should have exaclty one time schedule
          return acc
        }
        if (timeSchedules[0].availableQuantity === 0) {
          return acc
        }
        acc.push(Number(entry[0]))
        return acc
      }, [])

      if (availableDays.length === 0) {
        return []
      }

      if (SINGLE_DAY_PRICE_MODES.includes(firstSchedule.priceMode)) {
        return [availableDays[0]]
      }

      // check if it is sellable every day of the reservation
      return availableDays.length !== dayDifference ? [] : availableDays
    }

    case 'shifted':
    case 'custom': {
      // TODO: implementation
      throw new Error('not implemented yet')
    }

    default: {
      assertNever(service.scheduleType)
    }
  }
}

export function getBoughtServicesTotal(
  boughtServices?: BoughtServicesRecord[number]
): number {
  if (!boughtServices) {
    return 0
  }

  return Object.values(boughtServices).reduce<number>(
    (acc, boughtService) => acc + boughtService.bought,
    0
  )
}

export const getTimeScheduleKey = (timeSchedule: TimeSchedule): string =>
  `${timeSchedule.startTime} - ${timeSchedule.endTime}`

export function groupByTimeSchedule(
  timeSchedule: TimeSchedule,
  bought: number,
  days: number[],
  service: Service
): BoughServiceByTimeSchedule {
  const key = getTimeScheduleKey(timeSchedule)

  return {
    [key]: {
      bought,
      days,
      serviceId: service.serviceId,
      serviceName: service.name,
      timeSchedule,
    },
  }
}

export function isFullDay(item: {
  startTime: string
  endTime: string
}): boolean {
  return item.startTime === '00:00' && item.endTime === '23:59'
}

export const adjustEndTime = (endTime: string) =>
  endTime === '23:59' ? '24:00' : endTime
